import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { DyslexiaThemeProvider, lazyWithRetry } from '@learnwithai/ui-shared';
import { reportReactError } from '@child/diagnostics';

// Chunk-resilient lazy (grand-review P1-14b): a post-deploy hashed-chunk rotation
// 404s the old chunks a long-lived tab still references. Plain React.lazy would
// throw to the ErrorBoundary → "Something went wrong" on the revenue path
// (dashboard / billing / checkout). lazyWithRetry retries once, then reloads once.
const lazy = lazyWithRetry;
import { BackgroundMusicProvider } from './music/BackgroundMusicProvider';

import ParentGuard from './guards/ParentGuard';
import AdminGuard from './guards/AdminGuard';
import { PUBLIC_LANDING_ENABLED } from './publicLaunchFlag';

import ConnectionStatus from './components/ConnectionStatus';
import CookieConsentBanner from './components/CookieConsentBanner';
import { useSunnyParent } from './parentTheme';

// ── Parent auth pages (eager — no double-paint on cold start) ──────────────
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// ── Parent pages (lazy) ─────────────────────────────────────────────────────
const LandingPage = lazy(() => import('./pages/LandingPage'));
const HoldingPage = lazy(() => import('./pages/HoldingPage'));
// Open signup (owner-locked 2026-08-30): the public root is the full marketing
// LandingPage and /register is self-serve. Invite codes remain an OPTIONAL path
// (school/tester codes, entered on /register). The legacy invite-only Coming
// Soon page is DELETED — its "currently invite-only" copy contradicted open
// signup — and /coming-soon redirects to /register so old links never 404.
//
// PUBLIC-LANDING GATE (2026-08-31): the marketing landing + open-signup funnel
// are deployed DARK on the pre-launch PROD build (VITE_PUBLIC_LANDING=false):
// the anonymous root serves a minimal HoldingPage instead of LandingPage —
// nothing to click into a wall while the invite wall is still up and checkout
// is unprovisioned. Default (dev/test/e2e) is ON, so the funnel is fully
// exercised. /about always serves the real LandingPage for preview/QA. Drop
// the prod build arg at go-live to reveal it. See publicLaunchFlag.ts.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ChildDetailPage = lazy(() => import('./pages/ChildDetailPage'));
const AddChildPage = lazy(() => import('./pages/AddChildPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const SuperAdminSetupPage = lazy(() => import('./pages/SuperAdminSetupPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const CustomContentPage = lazy(() => import('./pages/CustomContentPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const TeacherProgressPage = lazy(() => import('./pages/TeacherProgressPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AdminSupportPage = lazy(() => import('./pages/AdminSupportPage'));

const ArcadeSettingsPage = lazy(() => import('./pages/ArcadeSettingsPage'));

// Program onboarding wizard + Reading Check results (behind lwa_program_choice_v1;
// the pages self-guard and redirect to /dashboard when the flag is OFF).
const ChildSetupWizard = lazy(() => import('./pages/onboarding/ChildSetupWizard'));
const ProgramResultsPage = lazy(() => import('./pages/onboarding/ProgramResultsPage'));

const Spinner = () => (
  <div style={{ padding: 48, textAlign: 'center', color: '#8E8E93' }}>Loading…</div>
);

function ChildIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/students/${id}`} replace />;
}

function RootRoute() {
  if (localStorage.getItem('auth_token')) return <Navigate to="/dashboard" replace />;
  // Anonymous visitors: the public marketing LandingPage once launch is armed,
  // else a minimal pre-launch HoldingPage (see PUBLIC_LANDING_ENABLED above).
  const Page = PUBLIC_LANDING_ENABLED ? LandingPage : HoldingPage;
  return <Suspense fallback={<Spinner />}><Page /></Suspense>;
}

// Exported for the crash-reporting unit test (P1-14a) — mounting the full <App>
// drags in the music/audio providers, so the boundary is verified in isolation.
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Report a SANITIZED crash event (no parent/child PII — see diagnostics.ts).
    // web-app was previously blind to dashboard/billing/checkout white-screens
    // (grand-review P1-14a); mirror web-child + mobile.
    reportReactError(error, info.componentStack ?? undefined);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <h2>Something went wrong</h2>
          <p>Try going back or refreshing the page.</p>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{ margin: '16px auto 0', padding: '12px', maxWidth: 760, whiteSpace: 'pre-wrap', textAlign: 'left', color: '#1D1D1F', background: '#fff', border: '1px solid #E5E5EA', borderRadius: 8 }}>
              {this.state.error.name}: {this.state.error.message}
            </pre>
          )}
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }} style={{ marginTop: '16px', padding: '8px 24px', fontSize: '1rem', cursor: 'pointer' }}>
            Go Home
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Forces a full-page navigation to /learn so the ALB can route the request
// to web-child. React Router's SPA navigation bypasses the ALB entirely.
function LearnPortalRedirect() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.location.replace(pathname + search); }, [pathname, search]);
  return null;
}

export default function App() {
  // Sunny Light design (parent flag lwa_light_theme_v1, DEFAULT OFF). Stamp the
  // root attribute the index.html CSS-var override keys on, and paint the app
  // shell canvas from the active parent palette so the whole portal shifts when
  // a parent turns Sunny Light on (OFF stays the byte-identical grey light look).
  const { sunny, P } = useSunnyParent();
  useEffect(() => {
    try {
      const el = document.documentElement;
      if (sunny) el.setAttribute('data-lwa-theme', 'sunny');
      else el.removeAttribute('data-lwa-theme');
    } catch { /* SSR/tests without a document */ }
  }, [sunny]);
  return (
    <DyslexiaThemeProvider>
      <BackgroundMusicProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <div style={{ minHeight: '100vh', background: P.bg }}>
          <ConnectionStatus />
          <CookieConsentBanner />

          <Routes>
            {/* ── Public ───────────────────────────────────────────────── */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/about" element={<Suspense fallback={<Spinner />}><LandingPage /></Suspense>} />
            <Route path="/coming-soon" element={<Navigate to="/register" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-email" element={<Suspense fallback={<Spinner />}><VerifyEmailPage /></Suspense>} />
            <Route path="/teacher/:token" element={<Suspense fallback={<Spinner />}><TeacherProgressPage /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<Spinner />}><PrivacyPolicyPage /></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={<Spinner />}><TermsPage /></Suspense>} />
            <Route path="/support" element={<Suspense fallback={<Spinner />}><SupportPage /></Suspense>} />
            <Route path="/admin/invite/:token" element={<Suspense fallback={<Spinner />}><AcceptInvitePage /></Suspense>} />
            {/* ── Parent (auth required) ────────────────────────────────── */}
            <Route path="/dashboard" element={<ParentGuard><Suspense fallback={<Spinner />}><DashboardPage /></Suspense></ParentGuard>} />
            <Route path="/students/add" element={<ParentGuard><Suspense fallback={<Spinner />}><AddChildPage /></Suspense></ParentGuard>} />
            <Route path="/students/:id" element={<ParentGuard><Suspense fallback={<Spinner />}><ChildDetailPage /></Suspense></ParentGuard>} />
            <Route path="/students/:id/setup/*" element={<ParentGuard><Suspense fallback={<Spinner />}><ChildSetupWizard /></Suspense></ParentGuard>} />
            <Route path="/students/:id/results" element={<ParentGuard><Suspense fallback={<Spinner />}><ProgramResultsPage /></Suspense></ParentGuard>} />
            {/* Legacy redirects — keep old /children/* URLs working */}
            <Route path="/children/add" element={<Navigate to="/students/add" replace />} />
            <Route path="/children/:id" element={<ChildIdRedirect />} />
            <Route path="/settings" element={<ParentGuard><Suspense fallback={<Spinner />}><SettingsPage /></Suspense></ParentGuard>} />
            <Route path="/billing" element={<ParentGuard><Suspense fallback={<Spinner />}><BillingPage /></Suspense></ParentGuard>} />
            <Route path="/billing/success" element={<ParentGuard><Suspense fallback={<Spinner />}><BillingPage /></Suspense></ParentGuard>} />
            <Route path="/billing/cancel" element={<ParentGuard><Suspense fallback={<Spinner />}><BillingPage /></Suspense></ParentGuard>} />
            <Route path="/content" element={<ParentGuard><Suspense fallback={<Spinner />}><CustomContentPage /></Suspense></ParentGuard>} />
            <Route path="/arcade-settings" element={<ParentGuard><Suspense fallback={<Spinner />}><ArcadeSettingsPage /></Suspense></ParentGuard>} />

            {/* ── Admin ─────────────────────────────────────────────────── */}
            <Route path="/admin/setup" element={<Suspense fallback={<Spinner />}><SuperAdminSetupPage /></Suspense>} />
            <Route path="/admin/login" element={<Suspense fallback={<Spinner />}><AdminLoginPage /></Suspense>} />
            <Route path="/admin" element={<AdminGuard><Suspense fallback={<Spinner />}><AdminDashboardPage /></Suspense></AdminGuard>} />
            <Route path="/admin/users" element={<AdminGuard><Suspense fallback={<Spinner />}><UserManagementPage /></Suspense></AdminGuard>} />
            <Route path="/admin/support" element={<AdminGuard><Suspense fallback={<Spinner />}><AdminSupportPage /></Suspense></AdminGuard>} />

            {/* ── Child portal lives at web-child (separate ECS service).
                ALB routes /learn* → web-child, but SPA navigation within
                web-app bypasses the ALB. Force a full page load so the
                browser makes a new server request and the ALB can route it. */}
            <Route path="/learn/*" element={<LearnPortalRedirect />} />
            <Route path="/learn" element={<LearnPortalRedirect />} />

            {/* Catch-all: a mistyped/stale URL must never render a blank dead-end.
                RootRoute redirects to /dashboard when already authed. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </ErrorBoundary>
      </BrowserRouter>
      </BackgroundMusicProvider>
    </DyslexiaThemeProvider>
  );
}
